import { type WorkspaceLayer } from 'src/map-3d/components/map-3d-sidebar/shared';
import { type BoundingSphere, Cartographic, Math as CesiumMath } from 'cesium';
import { EARTH_RADIUS_METERS, initialPointCloudData } from '../constants';
import { type SwipeMapLayer } from 'src/swipe-map-3d';
import type { PointCloudDataType } from 'shared/types';
import { isUndefined } from 'lodash';

/**
 * Convert Cesium3DTileset bounding sphere to geographic bounds for 2D viewing.
 *
 * **Method**: Spherical approximation using arc length formula
 * - Convert 3D sphere radius to angular distance: `angle = radius / earth_radius`
 * - Apply uniform offset to center lat/lon: `bounds = center ± angular_distance`
 * - Uses WGS84 earth radius (6,378,137m) for conversion
 *
 * **Limitations**: ~0.5% accuracy, assumes spherical Earth, uniform lat/lon scaling
 *
 * @param boundingSphere - The bounding sphere from a Cesium3DTileset
 * @returns bounds array [west, south, east, north] in degrees
 */
export const convertBoundingSphereToBounds = (
  boundingSphere?: BoundingSphere,
): number[] | null => {
  if (!boundingSphere) {
    return null;
  }

  const center = boundingSphere.center;
  const radius = boundingSphere.radius;

  // Convert center to cartographic (lat/lon).
  const centerCartographic = Cartographic.fromCartesian(center);

  // Calculate approximate degrees offset based on radius.
  // This is a rough approximation for 2D zoom.
  const radiusInRadians = radius / EARTH_RADIUS_METERS;

  const west = CesiumMath.toDegrees(
    centerCartographic.longitude - radiusInRadians,
  );
  const east = CesiumMath.toDegrees(
    centerCartographic.longitude + radiusInRadians,
  );
  const south = CesiumMath.toDegrees(
    centerCartographic.latitude - radiusInRadians,
  );
  const north = CesiumMath.toDegrees(
    centerCartographic.latitude + radiusInRadians,
  );

  return [west, south, east, north];
};

/**
 * Extract metadata properties from a point cloud layer
 * @param layer - The workspace layer
 * @returns properties object with bounds, minzoom, maxzoom
 */
export const getPointCloudMetadata = (
  layer: WorkspaceLayer | SwipeMapLayer,
) => {
  if (layer.mapLayer && 'boundingSphere' in layer.mapLayer) {
    try {
      const tileset = layer.mapLayer;
      const bounds = convertBoundingSphereToBounds(tileset.boundingSphere);

      if (bounds) {
        return {
          bounds,
          minzoom: 0,
          maxzoom: 22,
        };
      }
    } catch (error) {
      console.warn('Could not extract point cloud bounds:', error);
    }
  }
  return null;
};

/**
 * Gets point cloud properties from a selected layer, returning defaults if not available.
 * This is a pure utility function that can be used with useMemo for memoization.
 *
 * @param selectedPointCloudLayer - The selected point cloud layer
 * @returns Point cloud properties (pointSize, opacity, pointShape) or defaults
 */
export const getPointCloudProperties = (
  selectedPointCloudLayer: WorkspaceLayer | null | undefined,
): Required<PointCloudDataType> => {
  if (
    !selectedPointCloudLayer?.pointCloudData ||
    isUndefined(selectedPointCloudLayer.pointCloudData)
  ) {
    return initialPointCloudData;
  }

  const pointCloudData = {
    ...initialPointCloudData,
    ...selectedPointCloudLayer.pointCloudData,
  };
  return pointCloudData;
};
